import React from "react";
import { InputBasic } from "../../../components/utils/InputBasic";
import { InputTextArea } from "../../../components/utils/InputTextArea";
import { DatePicker } from "../../../components/utils/DatePicker";
import { Checkbox } from "../../../components/utils/Checkbox";

/**
 * BlockBuster component dynamically renders form input elements based on the attributes in `axt`.
 * It determines the appropriate input type (`glyph`, `epoch`, `arbiter`) and applies relevant properties.
 * 
 * ## Key Functionalities:
 * - Handles Dynamic Input Rendering: Maps attributes (`axt`) to input components.
 * - Decodes and Parses Properties: Extracts additional metadata from `axt.properties`.
 * - Retrieves Input Values & Errors: Uses `retrieveFacetXID` to fetch stored values and validation errors.
 * - Handles Input State Updates: Connects fields to `changeHandler` and `blurHandler`.
 * 
 * @param {Object} axt - The attribute definition for rendering the input block.
 * @param {string} oid - The identifier associated with the input field.
 * @param {Object[]} datum - The preview dataset containing field metadata.
 * @param {Object[]} input - The input state containing user-entered values.
 * @param {Object[]} faults - The validation state containing field errors.
 * @param {boolean} posting - Indicates whether a submission is in progress, disabling inputs if `true`.
 * @param {Function} changeHandler - The event handler for tracking input value changes.
 * @param {Function} blurHandler - The event handler for handling validation on input blur.
 * @param {Function} optionHandler - The event handler for checkbox and radio button inputs.
 * @returns {JSX.Element} - The dynamically rendered input field or block.
 */
export const BlockBuster = ({ axt, oid, datum, input, faults, posting, changeHandler, blurHandler, optionHandler, dateHandler }: any): JSX.Element => {
    let axtProps: any;

    try {
        const decodedProperties = atob(axt.properties);
        axtProps = JSON.parse(decodedProperties || "{}");
    } catch {
        console.error("Failed to parse attribute properties");
        axtProps = {};
    }

    let activeDatum = null
    let latentFault = null

    const defaultProperties = {
        mandatory: axtProps.mandatory || false,
        placeholder: "Sample text input",
        data_type: {
            name: "",
            icon: "",
        },
    };

    /**
     * Retrieves the stored value and associated label (`k`) for a given `xid`.
     * 
     * - Searches `input` and `faults` for the corresponding field value.
     * - Matches `xid` in the `datum.preview` to find the user-friendly label (`k`).
     * - Returns `{ value, k }` where:
     *   - `value` is the stored input or error message.
     *   - `k` is the display label for the field.
     * 
     * @param {string} xid - The attribute identifier within the form section.
     * @param {Object[]} spfcArray - The dataset containing input values or validation errors.
     * @returns {{ value: any, k: string | null }} - The retrieved field value and its label.
     */
    const retrieveFacetXID = (xid: string, spfcArray: any[]): { value: any; k: string | null; } => {
        const oidObject = spfcArray.find((obj: any) => obj[oid]);

        if (oidObject) {
            const attributes = oidObject[oid];
            const attribute = attributes.find((attr: any) => Object.keys(attr)[0] === xid);

            if (attribute) {
                const value = attribute[xid];

                // Find the corresponding label (`k`) from the `preview` dataset
                const section = datum.preview.find((item: any) => item.oid === oid);
                const k = section?.axt.find((attr: any) => attr.xid === xid)?.k || null;

                return { value, k };
            } else {
                console.warn(`No attribute found for xid: ${xid}`);
                return { value: undefined, k: null };
            }
        } else {
            console.warn(`No attributes found for oid: ${oid}`);
            return { value: undefined, k: null };
        }
    };

    /**
     * Resolves and assigns properties based on the block type.
     * 
     * - Modifies placeholder text and field constraints.
     * - Supports `glyph` (text fields), `epoch` (date fields), and `arbiter` (checkboxes).
     * 
     * @param {string} block - The block type (`glyph`, `epoch`, `arbiter`).
     * @returns {Object} - The resolved properties for the given block.
     */
    const resolveProperties = (block: string): object => {
        let properties = { ...defaultProperties };

        activeDatum = retrieveFacetXID(axt.xid, input);
        latentFault = retrieveFacetXID(axt.xid, faults);

        switch (block) {
            case "glyph":
                properties.placeholder = axt.label;
                properties.data_type = axtProps.restrictions || {};
                break;

            case "epoch":
                properties.placeholder = "Select a date";
                properties.data_type = { icon: "fa-calendar", name: "Date" };
                break;

            case "arbiter":
                properties = axtProps;
                break;

            default:
                console.warn(`Unhandled block type: ${block}`);
                break;
        }

        return properties;
    };

    /**
     * Selects the value of a specific field in the array based on the provided `oid` and `name`.
     * 
     * @param {Array} spfcArray - The array of to search through. Each contains multiple fields under an `oid` key.
     * @param {string} oid - The identifier for the section that holds the field you're interested in.
     * @param {string} xid - The name of the field whose value you want to retrieve.
     * 
     * @returns {any | undefined} - Returns the value of the field if it exists, or `undefined` if the field is not found.
     */
    const selectFacet = (spfcArray: any[], oid: string, xid: string): any | undefined => {
        for (const section of spfcArray) {
            if (section[oid]) {
                const field = section[oid].find((field: any) => Object.keys(field)[0] === xid);
                if (field) {
                    return field[xid]; // Return the value of the field
                }
            }
        }
        return undefined;
    };

    const properties: any = resolveProperties(axt.block);

    const generateValue = (label: string) =>
        label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

    const labelSubtitle = (
        <div className="w-full">
            <div className="w-full flex text-sm flex-row items-center align-middle gap-x-2 pb-1">
                <h3 className="flex-grow dark:text-slate-300">
                    {axt.label}:
                    {properties.mandatory ? <sup className="text-red-500">*</sup> : null}
                </h3>
            </div>

            {axt.subtitle && (
                <div className="text-xs flex-grow text-slate-500 dark:text-slate-400 mb-3">
                    <span className="block text-xs">{axt.subtitle}</span>
                </div>
            )}
        </div>
    );

    return (
        <li key={axt.xid} className="text-sm w-full flex flex-col pt-2 pb-2">
            {axt.block === "glyph" ? (
                properties.multiline ? (
                    <InputTextArea
                        name={axt.xid}
                        required={properties.mandatory}
                        label={labelSubtitle}
                        posting={posting}
                        value={activeDatum.value}
                        error={latentFault.value}
                        placeHolder={properties.placeholder}
                        blurHandler={(e: any) => blurHandler(e, oid, activeDatum.k)}
                        changeHandler={(e: any) => changeHandler(e, oid)}
                    />
                ) : (
                    <InputBasic
                        name={axt.xid}
                        required={properties.mandatory}
                        label={labelSubtitle}
                        posting={posting}
                        value={activeDatum.value}
                        error={latentFault.value}
                        placeHolder={properties.placeholder}
                        blurHandler={(e: any) => blurHandler(e, oid, activeDatum.k)}
                        changeHandler={(e: any) => changeHandler(e, oid)}
                    />
                )
            ) : axt.block === "epoch" ? (
                <DatePicker
                    value={activeDatum.value}
                    error={latentFault.value}
                    label={labelSubtitle}
                    name={axt.xid}
                    placeHolder={properties.placeholder}
                    disabled={false}
                    required={properties.mandatory}
                    changeHandler={(e: any) => changeHandler(e, oid)}
                    dateHandler={(e: any, close: () => void) => dateHandler(e, oid, axt.xid, close)}
                />
            ) : axt.block === "arbiter" ? (
                <>
                    <div className="block text-sm leading-6 text-primary-dark dark:text-slate-400">
                        {labelSubtitle}
                    </div>

                    <ul className="space-y-3 md:pl-3">
                        {
                            properties.options.map((option: any, index: number) => {
                                const value = generateValue(option.label); // Generate value dynamically
                                const stateValue = selectFacet(input, oid, axt.xid)
                                const isChecked = stateValue.includes(value) || false;

                                return (
                                    <li key={`option-${index}-lst-${option.label}`}>
                                        <Checkbox
                                            value={value}
                                            name={axt.xid}
                                            disabled={false}
                                            posting={posting}
                                            checked={isChecked}
                                            required={properties.mandatory}
                                            changeHandler={(e: any) => optionHandler(e, oid)}
                                            label={
                                                <>
                                                    <div className="font-medium text-slate-700 dark:text-slate-400">
                                                        <p className="block">{option.label.replace(" [SYS]", "")}</p>
                                                    </div>

                                                    {option.hint && (
                                                        <div className="md:mt-1 text-slate-500">{option.hint}</div>
                                                    )}
                                                </>
                                            }
                                        />
                                    </li>
                                )
                            })
                        }
                    </ul>

                    {
                        latentFault.value.length > 0 ? (
                            <span className='invalid-feedback text-xs md:pl-3 text-red-600 pt-3'>
                                {latentFault.value}
                            </span>
                        ) : null
                    }
                </>
            ) : (
                labelSubtitle
            )}
        </li>
    );
};
